import "twin.macro";

import { useAuth0 } from "@auth0/auth0-react";
import { t, TFunction } from "i18next";
import {
  ArrowDown,
  ArrowUp,
  BatteryFullIcon,
  BatteryLowIcon,
  BatteryMediumIcon,
  BatteryWarningIcon,
  CirclePause,
  Clock3Icon,
  Loader2,
  LockKeyhole,
  Pause,
  Power,
  RefreshCcw,
  RotateCcw,
  ShieldQuestionIcon,
  X,
} from "lucide-react";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import tw from "twin.macro";
import { useMediaQuery } from "usehooks-ts";

import { ModuleCommandEnum } from "@/api/events/modules/command.enum";
import { CommandTrackingView } from "@/api/events/modules/command-tracking";
import { InferredStateEnum } from "@/api/events/modules/inferred-transition-state.enum";
import { isLockedState } from "@/api/events/modules/isLockedState";
import { isPausedState } from "@/api/events/modules/isPausedStateEnum";
import { ModuleStateEnum } from "@/api/events/modules/module-state.enum";
import { ModuleTrackingView } from "@/api/events/modules/module-tracking";
import { useSendCommand } from "@/api/modules/mutations";
import OpenLockIcon from "@/components/open-lock";
import ProgressiveHorizontalArrows from "@/components/ProgressiveHorizontalArrows";
import ProgressiveVerticalArrow from "@/components/ProgressiveVerticalArrow";
import { useAreaContext } from "@/hooks/useAreaContext";
import { useAutoFitText } from "@/hooks/useAutoFitText";
import { useModuleDetailContext } from "@/hooks/useModuleDetailContext";

import BottomSheetErrorList from "./bottom-sheet-error-list";
import ErrorListModal from "./error-list-modal";
import Skeleton from "./skeleton";

type CardinalActionButtonProps = {
  isDisabled: boolean;
  iconNode: React.ReactNode;
  translationKey: "up" | "down" | "open" | "close";
  action: ModuleCommandEnum;
  postName: string;
  onAction: (postName: string, action: ModuleCommandEnum) => void;
};

/**
 * Auto-fitting label for the cardinal buttons. The cap (`maxWidth`) is set
 * a bit smaller than the button's diameter so the text doesn't bleed into
 * the rounded corners before shrinking.
 */
const CardinalActionLabel = ({
  text,
  maxWidth,
}: {
  text: string;
  maxWidth: number;
}) => {
  const { ref } = useAutoFitText<HTMLSpanElement>({
    maxFontSize: 12,
    minFontSize: 8,
    deps: [text, maxWidth],
  });
  return (
    <div style={{ width: maxWidth }}>
      <span ref={ref} tw="block font-medium text-center leading-tight">
        {text}
      </span>
    </div>
  );
};

type TransitionSnapshot = {
  isModuleLockedState: boolean;
  inferredState: InferredStateEnum;
  isRefreshing: boolean;
  isPending: boolean;
};

/** once omis ou true : un seul déclenchement puis retrait. once: false : abonnement récurrent. */
type TransitionSubscription = {
  shouldTrigger: (
    previous: TransitionSnapshot,
    current: TransitionSnapshot
  ) => boolean;
  onTransition: () => void;
  once?: boolean;
};

const CardinalActionButton = ({
  iconNode,
  translationKey,
  action,
  postName,
  isDisabled,
  onAction,
}: CardinalActionButtonProps) => {
  return (
    <button
      onClick={() => onAction(postName, action)}
      disabled={isDisabled}
      css={[
        tw`w-16 h-16 rounded-full bg-white border border-gray-200 shadow-md flex flex-col items-center justify-center gap-0.5 text-slate-700 transition-all`,
        tw`hover:shadow-lg hover:border-gray-300`,
        tw`active:scale-95 active:bg-gray-50`,
        tw`disabled:opacity-50 disabled:cursor-not-allowed`,
      ]}
    >
      <span tw="text-xl leading-none">{iconNode}</span>
      <CardinalActionLabel
        text={t(`moduleMaintenance.actions.${translationKey}`)}
        maxWidth={48}
      />
    </button>
  );
};

const getModuleImageName = (moduleState: InferredStateEnum) => {
  switch (moduleState) {
    case InferredStateEnum.FOLDED:
      return "folded";
    case InferredStateEnum.UP:
      return "up";
    case InferredStateEnum.UNFOLDED:
      return "unfolded";
    case InferredStateEnum.CLOSING:
      return "unfolded";
    case InferredStateEnum.OPENING:
      return "unfolded";
    case InferredStateEnum.PROGRESS_UP:
      return "up";
    case InferredStateEnum.PROGRESS_DOWN:
      return "folded";
    default:
      return "default";
  }
};

const getLedState = (moduleState: InferredStateEnum) => {
  switch (moduleState) {
    case InferredStateEnum.FOLDED:
      return (
        <div tw="rounded-full w-3 h-3 bg-modulesLeds-folded text-modulesLeds-folded" />
      );
    case InferredStateEnum.UP:
      return (
        <div tw="rounded-full w-3 h-3 bg-modulesLeds-up text-modulesLeds-up" />
      );
    case InferredStateEnum.UNFOLDED:
      return (
        <div tw="rounded-full w-3 h-3 bg-modulesLeds-unfolded text-modulesLeds-unfolded" />
      );
    case InferredStateEnum.ERROR:
      return (
        <div tw="rounded-full w-3 h-3 bg-modulesLeds-error text-modulesLeds-error" />
      );
    case InferredStateEnum.LOCKED:
      return <div tw="rounded-full w-3 h-3 bg-gray-500 text-gray-500" />;
    case InferredStateEnum.WAITING_FOR_STATE:
    case InferredStateEnum.UNFOLDING:
    case InferredStateEnum.FOLDING:
    case InferredStateEnum.OPENING:
    case InferredStateEnum.CLOSING:
    case InferredStateEnum.PROGRESS_UP:
    case InferredStateEnum.PROGRESS_DOWN:
    case InferredStateEnum.PAUSED:
      return (
        <div tw="rounded-full w-3 h-3 bg-modulesLeds-inProgress text-modulesLeds-inProgress" />
      );
    case InferredStateEnum.UNKNOWN:
      return <div tw="rounded-full w-3 h-3 bg-gray-500 text-gray-500" />;
  }
};

const getStateTranslation = (
  moduleState: InferredStateEnum,
  translator: TFunction<"translation", undefined>
) => {
  switch (moduleState) {
    case InferredStateEnum.FOLDED:
      return translator("moduleMaintenance.state.FOLDED");
    case InferredStateEnum.UP:
      return translator("moduleMaintenance.state.UP");
    case InferredStateEnum.UNFOLDED:
      return translator("moduleMaintenance.state.UNFOLDED");
    case InferredStateEnum.ERROR:
      return translator("moduleMaintenance.state.ERROR");
    case InferredStateEnum.WAITING_FOR_STATE:
      return translator("moduleMaintenance.state.WAITING_FOR_STATE");
    case InferredStateEnum.PAUSED:
      return translator("moduleMaintenance.state.PAUSED");
    case InferredStateEnum.UNFOLDING:
      return translator("moduleMaintenance.state.UNFOLDING");
    case InferredStateEnum.FOLDING:
      return translator("moduleMaintenance.state.FOLDING");
    case InferredStateEnum.OPENING:
      return translator("moduleMaintenance.state.OPENING");
    case InferredStateEnum.CLOSING:
      return translator("moduleMaintenance.state.CLOSING");
    case InferredStateEnum.PROGRESS_UP:
      return translator("moduleMaintenance.state.PROGRESS_UP");
    case InferredStateEnum.PROGRESS_DOWN:
      return translator("moduleMaintenance.state.PROGRESS_DOWN");
    case InferredStateEnum.UNKNOWN:
      return translator("moduleMaintenance.state.UNKNOWN");
  }
};

const getStateOfChargeIcon = (stateOfCharge: number | undefined) => {
  if (!stateOfCharge) {
    return <BatteryWarningIcon tw="w-5 h-5" color="gray" />;
  }

  if (stateOfCharge && stateOfCharge < 30) {
    return <BatteryLowIcon tw="w-5 h-5" color="red" />;
  } else if (stateOfCharge && stateOfCharge > 60) {
    return <BatteryFullIcon tw="w-5 h-5" color="green" />;
  } else {
    return <BatteryMediumIcon tw="w-5 h-5" color="orange" />;
  }
};

export const getAnimationByState = (
  state: InferredStateEnum,
  moduleState: ModuleStateEnum
) => {
  if (isLockedState(moduleState)) {
    return (
      <div tw="flex items-center gap-2 absolute inset-0 w-full h-full">
        <LockKeyhole tw="w-full h-full text-gray-500/70 opacity-100" />
      </div>
    );
  }

  if (isPausedState(moduleState)) {
    return (
      <div tw="flex items-center gap-2 absolute inset-0 w-full h-full">
        <CirclePause tw="w-full h-full text-gray-500/70 opacity-100" />
      </div>
    );
  }

  switch (state) {
    case InferredStateEnum.UNFOLDING:
      // Composite : ouverture (horizontal divergent) + montée (vertical up)
      return (
        <>
          <div tw="absolute inset-0 flex items-center justify-center pointer-events-none">
            <ProgressiveHorizontalArrows mode="divergent" tw="h-full w-auto" />
          </div>
          <div tw="absolute inset-0 flex items-center justify-center pointer-events-none">
            <ProgressiveVerticalArrow direction="up" tw="h-full w-auto" />
          </div>
        </>
      );
    case InferredStateEnum.FOLDING:
      // Composite : fermeture (horizontal convergent) + descente (vertical down)
      return (
        <>
          <div tw="absolute inset-0 flex items-center justify-center pointer-events-none">
            <ProgressiveHorizontalArrows mode="convergent" tw="h-full w-auto" />
          </div>
          <div tw="absolute inset-0 flex items-center justify-center pointer-events-none">
            <ProgressiveVerticalArrow direction="down" tw="h-full w-auto" />
          </div>
        </>
      );
    case InferredStateEnum.OPENING:
      return (
        <div tw="absolute inset-0 flex items-center justify-center pointer-events-none">
          <ProgressiveHorizontalArrows mode="divergent" tw="h-full w-auto" />
        </div>
      );
    case InferredStateEnum.CLOSING:
      return (
        <div tw="absolute inset-0 flex items-center justify-center pointer-events-none">
          <ProgressiveHorizontalArrows mode="convergent" tw="h-full w-auto" />
        </div>
      );
    case InferredStateEnum.PROGRESS_UP:
      return (
        <div tw="absolute inset-0 flex items-center justify-center pointer-events-none">
          <ProgressiveVerticalArrow direction="up" tw="h-full w-auto" />
        </div>
      );
    case InferredStateEnum.PROGRESS_DOWN:
      return (
        <div tw="absolute inset-0 flex items-center justify-center pointer-events-none">
          <ProgressiveVerticalArrow direction="down" tw="h-full w-auto" />
        </div>
      );
    case InferredStateEnum.ERROR:
      return (
        <div tw="flex items-center gap-2 absolute inset-0 w-full h-full">
          <X tw="w-full h-full text-red-500/70" strokeWidth={0.75} />
        </div>
      );
    case InferredStateEnum.WAITING_FOR_STATE:
      return (
        <div tw="flex items-center gap-2 absolute inset-0 w-full h-full">
          <Loader2 tw="w-full h-full text-gray-500/70 animate-spin" />
        </div>
      );
    case InferredStateEnum.UNKNOWN:
      return (
        <div tw="flex items-center gap-2 absolute inset-0 w-full h-full">
          <ShieldQuestionIcon
            tw="w-full h-full text-gray-500/70"
            strokeWidth={0.75}
          />
        </div>
      );
  }
};

const isModuleInProgress = (machineState: InferredStateEnum) => {
  return [
    InferredStateEnum.UNFOLDING,
    InferredStateEnum.FOLDING,
    InferredStateEnum.OPENING,
    InferredStateEnum.CLOSING,
    InferredStateEnum.PROGRESS_UP,
    InferredStateEnum.PROGRESS_DOWN,
  ].includes(machineState);
};

const getInferredStateFromMachineState = (
  machineState: ModuleStateEnum,
  lastCommand: CommandTrackingView
): InferredStateEnum => {
  switch (machineState) {
    case ModuleStateEnum.FOLDED:
      return InferredStateEnum.FOLDED;
    case ModuleStateEnum.UP:
      return InferredStateEnum.UP;
    case ModuleStateEnum.UNFOLDED:
      return InferredStateEnum.UNFOLDED;
    case ModuleStateEnum.FOLDED_STEP_1:
    case ModuleStateEnum.FOLDED_STEP_2:
    case ModuleStateEnum.FOLDED_STEP_3:
    case ModuleStateEnum.FOLDED_STEP_4:
    case ModuleStateEnum.FOLDED_STEP_5:
      return ModuleCommandEnum.RESET === lastCommand.command
        ? InferredStateEnum.PROGRESS_DOWN
        : getInferredStateFromCommand(lastCommand);
    case ModuleStateEnum.UNFOLDED_STEP_1:
    case ModuleStateEnum.UNFOLDED_STEP_2:
      return ModuleCommandEnum.RESET === lastCommand.command
        ? InferredStateEnum.PROGRESS_UP
        : getInferredStateFromCommand(lastCommand);
    case ModuleStateEnum.UNFOLDED_STEP_3:
      return ModuleCommandEnum.RESET === lastCommand.command
        ? InferredStateEnum.OPENING
        : getInferredStateFromCommand(lastCommand);
    case ModuleStateEnum.PAUSED_FOLDED:
      return InferredStateEnum.PAUSED;
    case ModuleStateEnum.PAUSED_UP:
      return InferredStateEnum.PAUSED;
    case ModuleStateEnum.PAUSED_UNFOLDED:
      return InferredStateEnum.PAUSED;
    case ModuleStateEnum.PAUSED_UNFOLDED_STEP_1:
    case ModuleStateEnum.PAUSED_UNFOLDED_STEP_2:
    case ModuleStateEnum.PAUSED_UNFOLDED_STEP_3:
      return InferredStateEnum.PAUSED;
    case ModuleStateEnum.PAUSED_FOLDED_STEP_1:
    case ModuleStateEnum.PAUSED_FOLDED_STEP_2:
    case ModuleStateEnum.PAUSED_FOLDED_STEP_3:
    case ModuleStateEnum.PAUSED_FOLDED_STEP_4:
    case ModuleStateEnum.PAUSED_FOLDED_STEP_5:
      return InferredStateEnum.PAUSED;
    case ModuleStateEnum.LOCKED_FOLDED:
      return InferredStateEnum.FOLDED;
    case ModuleStateEnum.LOCKED_UP:
      return InferredStateEnum.UP;
    case ModuleStateEnum.LOCKED_UNFOLDED:
      return InferredStateEnum.UNFOLDED;
    case ModuleStateEnum.LOCKED_UNFOLDED_STEP_1:
    case ModuleStateEnum.LOCKED_UNFOLDED_STEP_2:
    case ModuleStateEnum.LOCKED_UNFOLDED_STEP_3:
      return getInferredStateFromCommand(lastCommand);
    case ModuleStateEnum.LOCKED_FOLDED_STEP_1:
    case ModuleStateEnum.LOCKED_FOLDED_STEP_2:
    case ModuleStateEnum.LOCKED_FOLDED_STEP_3:
    case ModuleStateEnum.LOCKED_FOLDED_STEP_4:
    case ModuleStateEnum.LOCKED_FOLDED_STEP_5:
      return getInferredStateFromCommand(lastCommand);
    case ModuleStateEnum.LOCKED_PAUSED_FOLDED:
      return InferredStateEnum.FOLDED;
    case ModuleStateEnum.LOCKED_PAUSED_UP:
      return InferredStateEnum.UP;
    case ModuleStateEnum.LOCKED_PAUSED_UNFOLDED:
      return InferredStateEnum.UNFOLDED;
    case ModuleStateEnum.LOCKED_PAUSED_UNFOLDED_STEP_1:
    case ModuleStateEnum.LOCKED_PAUSED_UNFOLDED_STEP_2:
    case ModuleStateEnum.LOCKED_PAUSED_UNFOLDED_STEP_3:
      return getInferredStateFromCommand(lastCommand);
    case ModuleStateEnum.LOCKED_PAUSED_FOLDED_STEP_1:
    case ModuleStateEnum.LOCKED_PAUSED_FOLDED_STEP_2:
    case ModuleStateEnum.LOCKED_PAUSED_FOLDED_STEP_3:
    case ModuleStateEnum.LOCKED_PAUSED_FOLDED_STEP_4:
    case ModuleStateEnum.LOCKED_PAUSED_FOLDED_STEP_5:
      return getInferredStateFromCommand(lastCommand);
    case ModuleStateEnum.ERROR:
      return InferredStateEnum.ERROR;
    case ModuleStateEnum.WAITING_FOR_STATE:
      return getInferredStateFromCommand(lastCommand);
    default:
      return InferredStateEnum.UNKNOWN;
  }
};

const getInferredStateFromCommand = (
  lastCommand: CommandTrackingView
): InferredStateEnum => {
  if (lastCommand.command === ModuleCommandEnum.OPEN) {
    return InferredStateEnum.OPENING;
  }
  if (lastCommand.command === ModuleCommandEnum.CLOSE) {
    return InferredStateEnum.CLOSING;
  }
  if (lastCommand.command === ModuleCommandEnum.UP) {
    return InferredStateEnum.PROGRESS_UP;
  }
  if (lastCommand.command === ModuleCommandEnum.DOWN) {
    return InferredStateEnum.PROGRESS_DOWN;
  }
  return InferredStateEnum.WAITING_FOR_STATE;
};

const getInferredState = (
  machineState: ModuleStateEnum,
  lastCommand: CommandTrackingView
): InferredStateEnum => {
  return getInferredStateFromMachineState(machineState, lastCommand);
};

export function ModuleMaintenance() {
  const { getAccessTokenSilently } = useAuth0();

  const { post } = useModuleDetailContext();
  const { area } = useAreaContext();
  const { mutate: sendCommand, isPending } = useSendCommand({
    areaId: area._id,
  });

  const { t: translator, i18n } = useTranslation();

  const [isErrorListOpened, setIsErrorListOpened] = useState(false);
  const [moduleTrackingState, setModuleTrackingState] =
    useState<ModuleTrackingView>();
  const [commandTrackingState, setCommandTrackingState] =
    useState<CommandTrackingView>();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [inferredState, setInferredState] = useState<InferredStateEnum>(
    InferredStateEnum.WAITING_FOR_STATE
  );
  const [isModuleLockedState, setIsModuleLockedState] = useState(false);
  const previousTransitionSnapshot = useRef<TransitionSnapshot>({
    isModuleLockedState: false,
    inferredState: InferredStateEnum.WAITING_FOR_STATE,
    isRefreshing: false,
    isPending: false,
  });
  const transitionSubscriptions = useRef<TransitionSubscription[]>([]);

  const isDesktop = useMediaQuery("(min-width: 822px)");

  const handleAction = (moduleAddress: string, action: ModuleCommandEnum) => {
    setIsRefreshing(true);
    console.log("Action envoyée :", action);
    sendCommand({ destinationAddress: moduleAddress, command: action });
  };

  const subscribeToTransition = (subscription: TransitionSubscription) => {
    transitionSubscriptions.current.push(subscription);
  };

  useEffect(() => {
    if (!commandTrackingState || !moduleTrackingState) {
      setIsRefreshing(true);
      return;
    }
    const inferredState = getInferredState(
      moduleTrackingState.machineState,
      commandTrackingState
    );
    setInferredState(inferredState);
    setIsModuleLockedState(isLockedState(moduleTrackingState.machineState));

    if (isModuleInProgress(inferredState)) {
      setIsRefreshing(true);
    } else {
      setIsRefreshing(false);
    }

    return;
  }, [commandTrackingState, moduleTrackingState]);

  useEffect(() => {
    const currentSnapshot: TransitionSnapshot = {
      isModuleLockedState,
      inferredState,
      isRefreshing,
      isPending,
    };
    const previousSnapshot = previousTransitionSnapshot.current;

    const remaining: TransitionSubscription[] = [];
    for (const subscription of transitionSubscriptions.current) {
      const shouldRun = subscription.shouldTrigger(
        previousSnapshot,
        currentSnapshot
      );

      if (shouldRun) {
        subscription.onTransition();
        if (subscription.once === false) {
          remaining.push(subscription);
        }
      } else {
        remaining.push(subscription);
      }
    }

    transitionSubscriptions.current = remaining;
    previousTransitionSnapshot.current = currentSnapshot;
  }, [isModuleLockedState, inferredState, isRefreshing, isPending]);

  useEffect(() => {
    let source: EventSource | null = null;
    // Si le composant se démonte avant que `getAccessTokenSilently()` ne
    // résolve, la cleanup tournera alors que `source` est encore `null`.
    // Sans ce flag, la promise résoudrait ensuite et créerait une SSE
    // orpheline que personne ne fermera — Chrome limite à ~6 connexions
    // SSE par host, donc accumuler ces fuites bloque toute l'app.
    let isCancelled = false;

    getAccessTokenSilently().then((token) => {
      if (isCancelled) return;

      const eventSource = new EventSource(
        `${import.meta.env.VITE_API_URL}/module-interactions/subscribe-notifications/area/${area._id}/module/${post.address.toString()}?token=${token}`
      );
      source = eventSource;

      eventSource.onopen = () => {
        console.log("Connecté au serveur");
      };

      eventSource.addEventListener("command-tracking", (e) => {
        const payload = JSON.parse(e.data) as CommandTrackingView;
        console.log(payload);
        setCommandTrackingState(payload);
      });

      eventSource.addEventListener("module-state-tracking", (e) => {
        const payload = JSON.parse(e.data) as ModuleTrackingView;
        console.log(payload);
        setModuleTrackingState(payload);
      });

      eventSource.onerror = (err) => {
        console.error("Erreur SSE :", err);
        setIsRefreshing(false);
      };

      // Filet de sécurité : si la cleanup a fini de tourner avant que ce
      // bloc `.then` n'arrive (timing serré entre la résolution du token et
      // un démontage immédiat), on ferme la SSE qu'on vient de créer.
      if (isCancelled) {
        console.log("Closing SSE (race-condition guard)");
        eventSource.close();
        source = null;
      }
    });

    return () => {
      isCancelled = true;
      if (source) {
        console.log("Closing SSE");
        source.close();
        source = null;
      }
    };
  }, [getAccessTokenSilently, area._id, post.address]);

  // Squelette de chargement
  if (!moduleTrackingState?.machineState || !commandTrackingState?.command) {
    return <Skeleton />;
  }

  // Maquette normale
  return (
    <div tw="bg-white shadow rounded-xl w-full flex flex-col items-center gap-6 p-2 pt-0 md:p-6">
      <div>
        <div tw="flex flex-col bg-white items-center justify-between w-full gap-4">
          <div tw="flex items-center justify-between gap-3 bg-gray-50 rounded-lg shadow p-2 min-w-0 w-full">
            {/* Left group : LED + état + machineState. Peut rétrécir, et le
                machineState tronque en "…" si la place vient à manquer. */}
            <div tw="flex items-center gap-2 min-w-0 flex-1">
              <span tw="shrink-0">{getLedState(inferredState)}</span>
              <span tw="text-gray-600 font-normal text-sm shrink-0">
                {getStateTranslation(inferredState, translator)}
              </span>
              <span tw="shrink-0">-</span>
              <span
                tw="text-gray-600 font-normal text-sm min-w-0 truncate"
                title={moduleTrackingState.machineState}
              >
                {moduleTrackingState.machineState}
              </span>
            </div>
            {/* Right group : batterie. Largeur fixe, pas de compression. */}
            <div tw="flex items-center gap-2 shrink-0">
              <span>
                {getStateOfChargeIcon(moduleTrackingState.stateOfCharge)}
              </span>
              <span tw="text-gray-600 font-normal text-sm">
                {moduleTrackingState.stateOfCharge
                  ? `${moduleTrackingState.stateOfCharge}%`
                  : "❔"}
              </span>
            </div>
          </div>
          <p tw="flex items-center gap-2 text-gray-500 font-normal text-sm">
            <Clock3Icon tw="w-5 h-5" />
            <span tw="text-gray-500 font-normal text-sm">
              {DateTime.fromISO(moduleTrackingState.createdAt)
                .setLocale(i18n.language)
                .toFormat(
                  `'${translator("moduleMaintenance.time.statusSince")}' dd MMMM yyyy '${translator("moduleMaintenance.time.statusAt")}' HH'h'mm`
                )}
            </span>
          </p>
          <div
            css={[
              tw`grid grid-cols-3 w-full h-12 rounded-2xl border border-[rgb(215,226,240)] bg-[rgb(247,251,255)] shadow-sm`,
            ]}
          >
            <button
              onClick={() =>
                handleAction(
                  post.address.toString(),
                  ModuleCommandEnum.GET_STATUS
                )
              }
              disabled={isPending}
              aria-label={t("moduleMaintenance.actions.refresh")}
              title={t("moduleMaintenance.actions.refresh")}
              css={[
                tw`h-12 px-2 flex items-center justify-center gap-2 text-slate-600 font-semibold transition-all`,
                tw`hover:bg-slate-50 hover:text-slate-900 hover:rounded-l-2xl`,
                tw`active:scale-[0.98] active:bg-slate-100`,
                tw`disabled:opacity-50 disabled:cursor-not-allowed`,
                tw`border-r border-[rgb(215,226,240)]`,
              ]}
            >
              <RefreshCcw
                tw="w-5 h-5 shrink-0"
                css={isRefreshing && tw`animate-spin`}
              />

              <span
                css={[
                  tw`hidden truncate`,
                  { "@media (min-width: 515px)": { display: "inline" } },
                ]}
              >
                {t("moduleMaintenance.actions.refresh")}
              </span>
            </button>

            <button
              onClick={() =>
                handleAction(post.address.toString(), ModuleCommandEnum.REBOOT)
              }
              disabled={isPending}
              aria-label={t("moduleMaintenance.actions.reboot")}
              title={t("moduleMaintenance.actions.reboot")}
              css={[
                tw`h-12 px-2 flex items-center justify-center gap-2 text-slate-600 font-semibold transition-all`,
                tw`hover:bg-slate-50 hover:text-slate-900`,
                tw`active:scale-[0.98] active:bg-slate-100`,
                tw`disabled:opacity-50 disabled:cursor-not-allowed`,
                tw`border-r border-[rgb(215,226,240)]`,
              ]}
            >
              <Power tw="w-5 h-5 shrink-0" />

              <span
                css={[
                  tw`hidden truncate`,
                  { "@media (min-width: 515px)": { display: "inline" } },
                ]}
              >
                {t("moduleMaintenance.actions.reboot")}
              </span>
            </button>

            <button
              onClick={() => {
                subscribeToTransition({
                  shouldTrigger: (previous, current) =>
                    !previous.isModuleLockedState &&
                    current.isModuleLockedState,
                  onTransition: () =>
                    toast.success(translator("action.successLockingModule")),
                });
                handleAction(post.address.toString(), ModuleCommandEnum.LOCK);
              }}
              disabled={isPending}
              aria-label="Maintenance"
              title="Maintenance"
              css={[
                tw`h-12 px-2 flex items-center justify-center gap-2 text-slate-600 font-semibold transition-all`,
                tw`hover:bg-slate-50 hover:text-slate-900 hover:rounded-2xl`,
                tw`active:scale-[0.98] active:bg-slate-100`,
                tw`disabled:opacity-50 disabled:cursor-not-allowed`,
                isModuleLockedState && tw`hidden`,
              ]}
            >
              <LockKeyhole tw="w-5 h-5 shrink-0" />

              <span
                css={[
                  tw`hidden truncate`,
                  { "@media (min-width: 515px)": { display: "inline" } },
                ]}
              >
                Maintenance
              </span>
            </button>

            <button
              onClick={() => {
                subscribeToTransition({
                  shouldTrigger: (previous, current) =>
                    previous.isModuleLockedState &&
                    !current.isModuleLockedState,
                  onTransition: () =>
                    toast.success(translator("action.successUnlockingModule")),
                });
                handleAction(post.address.toString(), ModuleCommandEnum.UNLOCK);
              }}
              disabled={isPending}
              aria-label="Maintenance"
              title="Maintenance"
              css={[
                tw`h-12 px-2 flex items-center justify-center gap-2 text-slate-600 font-semibold transition-all`,
                tw`hover:bg-slate-50 hover:text-slate-900 hover:rounded-2xl`,
                tw`active:scale-[0.98] active:bg-slate-100`,
                tw`disabled:opacity-50 disabled:cursor-not-allowed`,
                !isModuleLockedState && tw`hidden`,
              ]}
            >
              <OpenLockIcon tw="w-5 h-5 shrink-0" />

              <span
                css={[
                  tw`hidden truncate`,
                  { "@media (min-width: 515px)": { display: "inline" } },
                ]}
              >
                Fin de maintenance
              </span>
            </button>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setIsErrorListOpened(true)}
        tw="flex items-center justify-center bg-red-500/80 rounded-md p-2 hidden"
        css={
          moduleTrackingState.machineErrors.length ||
          (moduleTrackingState.applicationErrors.length > 0 && tw`block`)
        }
      >
        <p tw="text-white text-sm">
          <span tw="">
            {translator("moduleMaintenance.errorListModal.toHandle")}
          </span>{" "}
          <span tw="text-white text-sm">→</span>
        </p>
      </button>

      {/* Icône d'Pantographe */}
      <div
        tw="flex items-center justify-center bg-greenFoundCanopee rounded-xl p-2 w-full max-w-40 h-36 relative"
        css={
          inferredState === InferredStateEnum.ERROR &&
          tw`border-2 border-red-500`
        }
      >
        <img
          src={`/posts/${getModuleImageName(inferredState)}.svg`}
          alt="Pantograph"
          tw="w-36 h-32"
        />

        {getAnimationByState(inferredState, moduleTrackingState.machineState)}
      </div>

      {/* Croix cardinale: Monter (N), Ouvrir (O), Fermer (E), Baisser (S) */}
      <div tw="grid grid-cols-3 grid-rows-3 gap-2 w-full max-w-[260px] mx-auto place-items-center">
        <div tw="col-start-2 row-start-1">
          <CardinalActionButton
            isDisabled={isRefreshing || isPending || isModuleLockedState}
            iconNode={<ArrowUp tw="w-6 h-6 text-emerald-600" />}
            translationKey="up"
            action={ModuleCommandEnum.UP}
            postName={post.address.toString()}
            onAction={handleAction}
          />
        </div>
        <div tw="col-start-1 row-start-2">
          <CardinalActionButton
            isDisabled={isRefreshing || isPending || isModuleLockedState}
            iconNode={<span aria-hidden>🌂</span>}
            translationKey="close"
            action={ModuleCommandEnum.CLOSE}
            postName={post.address.toString()}
            onAction={handleAction}
          />
        </div>
        {/* Centre: mini cercle vert avec l'icône du module (reflète l'état courant) */}
        <div
          tw="col-start-2 row-start-2 w-16 h-16 rounded-full bg-greenFoundCanopee flex items-center justify-center shadow-inner"
          aria-hidden
        >
          <img
            src={`/posts/${getModuleImageName(inferredState)}.svg`}
            alt=""
            tw="w-10 h-10"
          />
        </div>
        <div tw="col-start-3 row-start-2">
          <CardinalActionButton
            isDisabled={isRefreshing || isPending || isModuleLockedState}
            iconNode={<span aria-hidden>☂️</span>}
            translationKey="open"
            action={ModuleCommandEnum.OPEN}
            postName={post.address.toString()}
            onAction={handleAction}
          />
        </div>
        <div tw="col-start-2 row-start-3">
          <CardinalActionButton
            isDisabled={isRefreshing || isPending || isModuleLockedState}
            iconNode={<ArrowDown tw="w-6 h-6 text-emerald-600" />}
            translationKey="down"
            action={ModuleCommandEnum.DOWN}
            postName={post.address.toString()}
            onAction={handleAction}
          />
        </div>
      </div>

      {/* Barre pilule Pause / Reset (même style que la barre Refresh / Reboot / Cadenas)
          Sticky en bas du conteneur scrollable (sheet ou route) — toujours
          accessible sans avoir à scroller. Léger fond translucide + blur, le
          contenu peut passer derrière. */}
      <div
        css={[
          tw`grid grid-cols-2 w-full h-12 rounded-2xl border border-[rgb(215,226,240)] shadow-sm overflow-hidden`,
          tw`sticky bottom-0 z-10`,
          tw`bg-[rgb(247,251,255)]/85 backdrop-blur-sm`,
          tw`mt-auto`,
        ]}
      >
        <button
          onClick={() =>
            handleAction(post.address.toString(), ModuleCommandEnum.PAUSE)
          }
          disabled={isPending || isModuleLockedState}
          aria-label={t("moduleMaintenance.actions.pause")}
          title={t("moduleMaintenance.actions.pause")}
          css={[
            tw`h-12 px-2 flex items-center justify-center gap-2 text-slate-600 font-semibold transition-all`,
            tw`hover:bg-slate-50 hover:text-slate-900`,
            tw`active:scale-[0.98] active:bg-slate-100`,
            tw`disabled:opacity-50 disabled:cursor-not-allowed`,
            tw`border-r border-[rgb(215,226,240)]`,
          ]}
        >
          <Pause tw="w-5 h-5 shrink-0" />
          <span tw="truncate">{t("moduleMaintenance.actions.pause")}</span>
        </button>
        <button
          onClick={() =>
            handleAction(post.address.toString(), ModuleCommandEnum.RESET)
          }
          disabled={isPending || isModuleLockedState}
          aria-label={t("moduleMaintenance.actions.reset")}
          title={t("moduleMaintenance.actions.reset")}
          css={[
            tw`h-12 px-2 flex items-center justify-center gap-2 text-slate-600 font-semibold transition-all`,
            tw`hover:bg-slate-50 hover:text-slate-900`,
            tw`active:scale-[0.98] active:bg-slate-100`,
            tw`disabled:opacity-50 disabled:cursor-not-allowed`,
          ]}
        >
          <RotateCcw tw="w-5 h-5 shrink-0" />
          <span tw="truncate">{t("moduleMaintenance.actions.reset")}</span>
        </button>
      </div>

      {isDesktop ? (
        <ErrorListModal
          areaId={area._id}
          open={isErrorListOpened}
          setOpen={setIsErrorListOpened}
          errors={[
            ...moduleTrackingState.machineErrors,
            ...moduleTrackingState.applicationErrors,
          ]}
          moduleAddress={post.address.toString()}
        />
      ) : (
        <BottomSheetErrorList
          areaId={area._id}
          open={isErrorListOpened}
          setOpen={setIsErrorListOpened}
          errors={[
            ...moduleTrackingState.machineErrors,
            ...moduleTrackingState.applicationErrors,
          ]}
          moduleAddress={post.address.toString()}
        />
      )}
    </div>
  );
}
